import { useState, useRef, useEffect } from 'react';
import { FiSend, FiTrash2, FiMessageSquare, FiX } from 'react-icons/fi';
import useStore from '../store/useStore';
import aiService, { parseConfigCommands, AI_MODEL } from '../services/aiService';
import { getFunctionLabel } from '../constants/functions';
import './AIAssistant.css';
import { useToast } from './Toast';

/**
 * AI Assistant Component
 * Natural language configuration with the configured AI model (see AI_MODEL)
 */

/**
 * Build a fresh welcome message object. Used on first setup AND when the
 * conversation is cleared, so the same greeting shape is re-seeded
 * regardless of wording.
 */
function buildWelcomeMessage() {
    return {
        role: 'assistant',
        content: '👋 Hi! I\'m your Looper Plus AI assistant. I can help you configure your device, explain MIDI commands, and optimize your setup. Try asking me:\n\n• "Set footswitch 1 to mute"\n• "What does Program Change 5 do?"\n• "Help me set up for live performance"',
        timestamp: new Date()
    };
}

function AIAssistant() {
    const { currentProfile, setCurrentProfile, setProfileModified } = useStore();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [apiKey, setApiKey] = useState('');
    const [isConfigured, setIsConfigured] = useState(false);
    const messagesEndRef = useRef(null);
    const notify = useToast();

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSetupAPI = () => {
        if (!apiKey.trim()) {
            notify('Please enter your Gemini API key', 'info');
            return;
        }

        try {
            aiService.initialize(apiKey);
            setIsConfigured(true);
            setMessages([buildWelcomeMessage()]);
        } catch (error) {
            notify(`Failed to initialize AI: ${error.message}`, 'error');
        }
    };

    const handleSend = async () => {
        if (!input.trim() || isLoading) return;

        const userMessage = input.trim();
        setInput('');

        // Add user message
        setMessages(prev => [...prev, {
            role: 'user',
            content: userMessage,
            timestamp: new Date()
        }]);

        setIsLoading(true);

        try {
            const result = await aiService.sendMessage(userMessage, currentProfile);

            if (result.success) {
                const ops = parseConfigCommands(result.response);
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: result.response,
                    ops,
                    timestamp: new Date()
                }]);
            } else {
                setMessages(prev => [...prev, {
                    role: 'error',
                    content: `Error: ${result.error}`,
                    timestamp: new Date()
                }]);
            }
        } catch (error) {
            setMessages(prev => [...prev, {
                role: 'error',
                content: `Failed to get response: ${error.message}`,
                timestamp: new Date()
            }]);
        }

        setIsLoading(false);
    };

    // Human-readable label for a parsed config op (for the Apply button).
    const describeOp = (op) => {
        if (op.kind === 'footswitch') {
            return `Set ${op.target.toUpperCase()} to ${getFunctionLabel(op.function)}`;
        }
        if (op.kind === 'midiChannel') {
            return `Set MIDI channel to ${op.value}`;
        }
        if (op.kind === 'encoder') {
            return `Set encoder to ${getFunctionLabel(op.function)}`;
        }
        return 'Apply change';
    };

    // Apply a single parsed op to the current profile (with confirmation).
    const handleApplyOp = (op) => {
        if (!currentProfile) {
            notify('No profile loaded to apply changes to.', 'error');
            return;
        }
        if (!window.confirm(`Apply this change?\n\n${describeOp(op)}`)) return;

        let clone;
        if (op.kind === 'footswitch') {
            clone = {
                ...currentProfile,
                footswitches: {
                    ...currentProfile.footswitches,
                    [op.target]: {
                        ...currentProfile.footswitches[op.target],
                        function: op.function
                    }
                }
            };
        } else if (op.kind === 'midiChannel') {
            clone = {
                ...currentProfile,
                midiSettings: {
                    ...currentProfile.midiSettings,
                    channel: op.value
                }
            };
        } else if (op.kind === 'encoder') {
            clone = {
                ...currentProfile,
                encoder: {
                    ...currentProfile.encoder,
                    function: op.function
                }
            };
        } else {
            return;
        }

        // Apply to the working copy only — persistence goes through the explicit
        // Save / Save As flow in Profile Management, so unsaved templates are
        // never silently written to storage (same rule as ConfigurationEditor).
        setCurrentProfile(clone);
        setProfileModified(true);
        notify('Applied. Use Save in Profile Management to persist this change.', 'info');
    };

    const handleClear = () => {
        if (confirm('Clear conversation history?')) {
            // Re-seed a fresh welcome message regardless of greeting wording.
            setMessages([buildWelcomeMessage()]);
            aiService.clearHistory();
        }
    };

    return (
        <>
            {/* Floating Button */}
            {!isOpen && (
                <button
                    className="ai-fab"
                    onClick={() => setIsOpen(true)}
                    title="Open AI Assistant"
                >
                    <FiMessageSquare size={24} />
                    <span className="ai-badge">AI</span>
                </button>
            )}

            {/* AI Chat Panel */}
            {isOpen && (
                <div className="ai-panel glass">
                    <div className="ai-header">
                        <div className="ai-title">
                            <FiMessageSquare />
                            <span>AI Assistant</span>
                            <span className="ai-model-badge">{AI_MODEL.label}</span>
                        </div>
                        <button
                            className="ai-close"
                            onClick={() => setIsOpen(false)}
                            title="Close"
                        >
                            <FiX />
                        </button>
                    </div>

                    {!isConfigured ? (
                        <div className="ai-setup">
                            <h4>Setup Gemini AI</h4>
                            <p className="text-sm text-secondary">
                                Enter your Gemini API key to enable the AI assistant. Get a free key at{' '}
                                <a href="https://makersuite.google.com/app/apikey" target="_blank" rel="noopener noreferrer">
                                    Google AI Studio
                                </a>
                            </p>
                            <input
                                type="password"
                                value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                                placeholder="Enter Gemini API key..."
                                className="api-key-input"
                                onKeyDown={(e) => e.key === 'Enter' && handleSetupAPI()}
                            />
                            <button
                                className="btn-primary"
                                onClick={handleSetupAPI}
                                disabled={!apiKey.trim()}
                            >
                                Initialize AI
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="ai-messages">
                                {messages.map((msg, idx) => (
                                    <div key={idx} className={`ai-message ${msg.role}`}>
                                        <div className="message-content">
                                            {msg.content}
                                        </div>
                                        {msg.ops && msg.ops.length > 0 && (
                                            <div className="ai-config-actions">
                                                {msg.ops.map((op, opIdx) => (
                                                    <button
                                                        key={opIdx}
                                                        className="btn-apply-config"
                                                        onClick={() => handleApplyOp(op)}
                                                        title="Apply this configuration change"
                                                    >
                                                        Apply: {describeOp(op)}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        <div className="message-time">
                                            {msg.timestamp.toLocaleTimeString()}
                                        </div>
                                    </div>
                                ))}
                                {isLoading && (
                                    <div className="ai-message assistant">
                                        <div className="message-content">
                                            <div className="typing-indicator">
                                                <span></span>
                                                <span></span>
                                                <span></span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            <div className="ai-input-area">
                                <button
                                    className="btn-clear"
                                    onClick={handleClear}
                                    title="Clear conversation"
                                    disabled={messages.length === 0}
                                >
                                    <FiTrash2 />
                                </button>
                                <input
                                    type="text"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                                    placeholder="Ask me anything about your Looper Plus..."
                                    className="ai-input"
                                    disabled={isLoading}
                                />
                                <button
                                    className="btn-send"
                                    onClick={handleSend}
                                    disabled={!input.trim() || isLoading}
                                >
                                    <FiSend />
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </>
    );
}

export default AIAssistant;
